import { useQueryClient } from '@tanstack/react-query';
import { Plug, RefreshCw, ShoppingBasket } from 'lucide-react';
import { Trans, useTranslation } from 'react-i18next';
import { useNavigate, useParams } from 'react-router-dom';
import { QueryKey } from '@/Config/api/queryKey';
import { IndexPath } from '@/Config/app/router/index.path';
import { IntegrationEnabledBadge } from '@/Domain/components/Integration/IntegrationEnabledBadge';
import { IntegrationStatusBadge } from '@/Domain/components/Integration/IntegrationStatusBadge';
import { IntegrationIntroduction } from '@/Domain/components/Integration/components/IntegrationIntroduction';
import { IntegrationSection } from '@/Domain/components/Integration/components/IntegrationSection';
import { Button, IconButton } from '@/Domain/components/Shared/Button/Button';
import {
  Sheet,
  SheetContent,
  SheetHeader,
  SheetTitle,
} from '@/Domain/components/Shared/Sheet/sheet';
import CloseIcon from '@/Domain/components/icons/Close.icon';
import { ShopifyIcon } from '@/Domain/components/icons/Shopify.icon';
import { useShopifyConnection } from '@/Domain/hooks/Action/ShopifyMcp/useShopifyConnection.hook';
import { MODAL_NAMES, useModalStore } from '@/Domain/store/modal.store';
import {
  APP_SHOPIFY,
  OPEN_MODAL_QUERY_PARAM,
} from '@/UI/GettingStarted/components/onboardingOptions';

const SHOPIFY_APP_URL = 'https://apps.shopify.com/quickchat-ai';
const LINK_CLASS =
  'text-foreground link-underline underline-offset-2 transition-colors hover:text-muted-foreground';

interface ShopifySheetProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

// Shopify is wired up as a Shopify Remote MCP action, configured on the Actions &
// MCPs page. This sheet only explains that and deep-links to the same dialog the
// "Shopify MCP" button there opens (create-if-needed + open), rather than
// reproducing the store-URL form.
export const ShopifySheet = ({ open, onOpenChange }: ShopifySheetProps) => {
  const { t } = useTranslation('shopify_mcp');
  const navigate = useNavigate();
  const { scenario } = useParams<{ scenario: string }>();
  const queryClient = useQueryClient();
  const { toggleModal } = useModalStore();

  // Mirror the Shopify MCP action on the Actions & MCPs page: connected = a store
  // URL is configured (the action is valid), enabled = the action is active.
  const { shopifyAction } = useShopifyConnection();
  const isConnected = Boolean(shopifyAction?.is_valid);
  const isEnabled = Boolean(shopifyAction?.is_active);
  const hasShopifyAction = Boolean(shopifyAction);

  const actionsPath = scenario ? `/i/${scenario}/${IndexPath.actions}` : IndexPath.actions;

  // No action yet: deep-link to Actions & MCPs, which creates the Shopify MCP and
  // opens its dialog (same as the "Shopify MCP" create button there).
  const handleConnect = () => {
    onOpenChange(false);
    navigate(`${actionsPath}?${OPEN_MODAL_QUERY_PARAM}=${APP_SHOPIFY}`);
  };

  // Action exists: open its edit dialog (mirrors ShopifyMcpActionBox) and navigate
  // to Actions & MCPs where that dialog is mounted.
  const handleEdit = async () => {
    if (!shopifyAction) return;
    onOpenChange(false);
    const shopifyMcpAction = await queryClient.ensureQueryData({
      queryKey: [QueryKey.actions.shopifyMcp.get, shopifyAction.id],
    });
    toggleModal(MODAL_NAMES.SHOPIFY_MCP_DIALOG, { shopifyMcpAction });
    navigate(actionsPath);
  };

  return (
    <Sheet open={open} onOpenChange={onOpenChange}>
      <SheetContent
        className="max-w-xl w-full h-full pb-0"
        data-testid="shopify-sheet"
        onEscapeKeyDown={(e) => {
          e.preventDefault();
          onOpenChange(false);
        }}
      >
        <SheetHeader className="px-0.5 items-center flex-row">
          <div className="flex items-center gap-3">
            <ShopifyIcon className="size-8 shrink-0" />
            <div className="flex flex-col gap-1">
              <div className="flex items-center gap-2">
                <SheetTitle data-testid="shopify-sheet-title">Shopify</SheetTitle>
                <IntegrationStatusBadge connected={isConnected} />
                <IntegrationEnabledBadge enabled={isEnabled} />
              </div>
            </div>
          </div>
          <IconButton
            onClick={() => onOpenChange(false)}
            variant="secondary"
            data-testid="shopify-sheet-close"
            className="shrink-0"
          >
            <CloseIcon />
          </IconButton>
        </SheetHeader>

        <div className="overflow-y-auto px-0.5 pb-6 space-y-6">
          <IntegrationIntroduction
            description={t('overview')}
            prerequisites={(t('prerequisites', { returnObjects: true }) as string[]).map(
              (_, index) => (
                <Trans
                  key={index}
                  t={t}
                  i18nKey={`prerequisites.${index}`}
                  components={[
                    <a
                      key="shopifyapp"
                      href={SHOPIFY_APP_URL}
                      target="_blank"
                      rel="noopener noreferrer"
                      className={LINK_CLASS}
                    />,
                  ]}
                />
              ),
            )}
            docsHref="https://docs.quickchat.ai/channels/shopify"
          />

          <IntegrationSection
            title={t('integrations:section.connect')}
            data-testid="shopify-connect-enable-section"
          >
            {hasShopifyAction ? (
              <Button
                variant="secondary"
                size="sm"
                className="w-full"
                onClick={handleEdit}
                data-testid="shopify-edit-button"
              >
                <RefreshCw className="size-4" />
                {t('edit_button')}
              </Button>
            ) : (
              <div className="flex flex-col gap-2">
                <Button
                  variant="dark"
                  size="sm"
                  className="w-full"
                  asChild
                  data-testid="shopify-install-app-button"
                >
                  <a href={SHOPIFY_APP_URL} target="_blank" rel="noopener noreferrer">
                    <ShoppingBasket className="size-4" />
                    {t('install_app_button')}
                  </a>
                </Button>
                <span className="text-center text-sm text-muted-foreground">{t('or')}</span>
                <Button
                  variant="dark"
                  size="sm"
                  className="w-full"
                  onClick={handleConnect}
                  data-testid="shopify-connect-button"
                >
                  <Plug className="size-4" />
                  {t('connect_button')}
                </Button>
              </div>
            )}
          </IntegrationSection>
        </div>
      </SheetContent>
    </Sheet>
  );
};
